import { Component, ChangeDetectionStrategy, EventEmitter, ChangeDetectorRef, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { ExcludeFilterItems, FilterItems, SelectData, SelectItem, SelectedItemList, DateRangeFilter } from '../../interfaces/filter-data';
import { PortfolioFilterRequestData, ProtocolFilterData } from '../../../dashboard/overview-dashboard/dashboard-info';
import { SampleTrackingStorage } from '../../../sample-tracking-storage';
import { ReportColumnsService } from '../../services/report-columns-service';
import { DateRangeFilterValue, RangeKind } from '../../../../shared/time-frame-filter/timeframe-filter.component';

@Component({
    selector: 'app-filter-options',
    template: require('./filter-options.component.html'),
    styles: [require('./filter-options.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class FilterOptionsComponent implements OnChanges {

    @Input()
    public selectedFilterItems: SelectedItemList;

    @Input()
    public dateRangeTypes: DateRangeFilter[] = [];

    @Input()
    public clearAllSelectedItems: boolean;

    @Input()
    public showDataLoadingError: boolean;

    @Output()
    public onSelectedItemChange: EventEmitter<SelectedItemList> = new EventEmitter<SelectedItemList>();

    @Output()
    public onDateRangeTypeChange: EventEmitter<{ key: string, value: DateRangeFilterValue }> = new EventEmitter<null>();

    @Output()
    public onDataLoad: EventEmitter<boolean> = new EventEmitter<boolean>();
     
    public timeFrameRanges: RangeKind[];
    public datePickerOptions = {
        dateFormat: 'dd-mmm-yyyy',
        showTodayBtn: true,
        todayBtnTxt: 'Today'
    };
    public studyGroupData: SelectData;
    public protocolData: SelectData;
    public projectData: SelectData;
    public regionData: SelectData;
    public countryData: SelectData;
    public investigatorData: SelectData;
    public isSaveFilterEnabled = false;
    public isStudyGroupLoading = true;
    public isProtocolLoading = true;
    public isRegionLoading = true;
    public isProjectLoading = true;
    public isCountryLoading = true;
    public isInvestigatorLoading = true;
    public reportTypes = [
        { id: 1, label: 'Specimen Management Inventory Report' },
        { id: 2, label: 'Sample Tracking Report' }
    ];
    public selectedReportTypeId = 0;
    public showDateRange = false;
    public selectedDateRangeTypeKey: string;
    public selectedTimeFrameValue: DateRangeFilterValue;
    private selectedItemList: SelectedItemList = { filters: {} } as SelectedItemList;
    private allProtocolCodeAndSponsorName: PortfolioFilterRequestData[] = [];
    private studyGroupList: SelectItem[] = [];
    private studyGroupPortfolioData: ProtocolFilterData[] = [];
    private protocolFilterData: ProtocolFilterData[] = [];
    private regionFilterData: ProtocolFilterData[] = [];
    private projectFilterData: ProtocolFilterData[] = [];
    private countryFilterData: ProtocolFilterData[] = [];
    private reUpdateSelectedItems = false;
    constructor(
        private readonly sampleTrackingStorage: SampleTrackingStorage,
        private changeDectRef: ChangeDetectorRef,
        private reportcolumnService: ReportColumnsService,
    ) {
        this.getFilterOptions();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.selectedFilterItems) {
            this.clearDateRange();
            this.selectedReportTypeId = this.selectedFilterItems.reportType;
            if (changes.selectedFilterItems
                && this.selectedFilterItems.filters
                && Object.keys(this.selectedFilterItems.filters).length) {
                this.reUpdateSelectedItems = false;
                this.updateSelectedItems();
            }
            if (this.selectedFilterItems.dateRange) {
                this.timeFrameRanges = this.reportcolumnService.getDateRangeFilters();
                this.selectedDateRangeTypeKey = this.selectedFilterItems.dateRange.dateField;
                if (this.selectedFilterItems.dateRange.daysBack) {
                    this.selectedTimeFrameValue = this.reportcolumnService.getSelectedDateRangeByDaysBack(+this.selectedFilterItems.dateRange.daysBack);
                }
                if (this.selectedFilterItems.dateRange.fromDate || this.selectedFilterItems.dateRange.toDate) {
                        this.selectedTimeFrameValue = this.reportcolumnService.getSelectedDateRangeValue(this.selectedFilterItems.dateRange);
                }
                this.showDateRange = true; 
                this.onDateRangeTypeChange.emit({ key: this.selectedDateRangeTypeKey, value: this.selectedTimeFrameValue });
            }
        }

        if (changes.clearAllSelectedItems && this.clearAllSelectedItems) {
            this.selectedItemList.filters = {} as FilterItems;
            this.getFilterOptions();
        }
    }

    public onStudyGroupSelectChange(selectedItem: SelectItem[]) {
        this.selectedItemList.filters = {
            studyGroups: selectedItem
        } as FilterItems;

        let selectedProtocols = this.allProtocolCodeAndSponsorName;

        if (selectedItem && selectedItem.length) {
            selectedProtocols = selectedItem.map((si) => {
                return (this.studyGroupData.listItems.find(li => li.name === si.name));
            }).
                map(e => e.data.protocolCodeAndSponsorName).
                reduce((acc, val) => acc.concat(val), []);
        }

        this.getOtherFilterData(selectedProtocols, true);
    }

    public onProtocolSelectChange(selectedItem: SelectItem[]) {
        this.selectedItemList.filters = {
            studyGroups: this.selectedItemList.filters.studyGroups,
            protocol: selectedItem
        } as FilterItems;
        this.protocolFilterData = this.getSelectedFilterData(this.studyGroupPortfolioData, selectedItem, 'protocolCode');
        this.setFilterData(this.protocolFilterData, { protocol: true }, true);
    }

    public onRegionSelectChange(selectedItem: SelectItem[]) {
        this.selectedItemList.filters = {
            studyGroups: this.selectedItemList.filters.studyGroups,
            protocol: this.selectedItemList.filters.protocol,
            region: selectedItem
        } as FilterItems;
        this.regionFilterData = this.getSelectedFilterData(this.protocolFilterData, selectedItem, 'region');
        this.setFilterData(this.regionFilterData, { protocol: true, region: true }, true);
    }

    public onProjectSelectChange(selectedItem: SelectItem[]) {
        this.selectedItemList.filters = {
            studyGroups: this.selectedItemList.filters.studyGroups,
            protocol: this.selectedItemList.filters.protocol,
            region: this.selectedItemList.filters.region,
            project: selectedItem
        } as FilterItems;
        this.projectFilterData = this.getSelectedFilterData(this.protocolFilterData, selectedItem, 'projectNumber');
        this.setFilterData(this.projectFilterData, { protocol: true, region: true, project: true }, true);
    }

    public onCountrySelectChange(selectedItem: SelectItem[]) {
        this.selectedItemList.filters = {
            studyGroups: this.selectedItemList.filters.studyGroups,
            protocol: this.selectedItemList.filters.protocol,
            region: this.selectedItemList.filters.region,
            project: this.selectedItemList.filters.project,
            country: selectedItem
        } as FilterItems;
        this.countryFilterData = this.getSelectedFilterData(this.projectFilterData, selectedItem, 'countryName');
        this.setFilterData(this.countryFilterData, { protocol: true, region: true, project: true, country: true }, true);
    }

    public onInvestigatorSelectChange(selectedItem: SelectItem[]) {
        this.selectedItemList.filters = {
            ...this.selectedItemList.filters,
            investigator: selectedItem,
        } as FilterItems;
        const portfolioData = this.getSelectedFilterData(this.countryFilterData, selectedItem, 'investigatorName');
        this.setFilterData(portfolioData, { protocol: true, region: true, project: true, country: true, investigator: true }, true);
    }

    public onClearFiltersClick() {
        this.selectedReportTypeId = 0;
        this.selectedItemList.filters = {} as FilterItems;
        this.loadingOn();
        this.getFilterOptions(true);
    }

    public onReportTypeChange(reportType: { id: number, name: string }) {
        this.selectedItemList.reportType = reportType.id;
        this.onSelectedItemChange.emit(this.selectedItemList);
    }

    public onDateRangeButtonClick() {
        this.showDataLoadingError = false;
        if (!this.dateRangeTypes) {
            setTimeout(
                () => {
                    this.showDataLoadingError = false;
                },
                2000);
            this.showDataLoadingError = true;
            return;
        }
        this.showDateRange = !this.showDateRange;
        if (!this.showDateRange) {
            this.selectedDateRangeTypeKey = null;
            this.selectedTimeFrameValue = null;
            this.onDateRangeTypeChange.emit(null);
        }
        if (!this.selectedDateRangeTypeKey && this.showDateRange) {
            this.timeFrameRanges = this.reportcolumnService.getDateRangeFilters();
            this.dateRangeTypes[0].isChecked = true;
            this.selectedDateRangeTypeKey = this.dateRangeTypes[0].value;
            this.onDateRangeTypeChange.emit({ key: this.selectedDateRangeTypeKey, value: this.selectedTimeFrameValue });
        }
    }

    public onDateRangeChange(dateRange: DateRangeFilter) {
        this.selectedDateRangeTypeKey = dateRange.value;
        this.onDateRangeTypeChange.emit({ key: this.selectedDateRangeTypeKey, value: this.selectedTimeFrameValue });
    }

    public timeFrameFilterChange($event: DateRangeFilterValue) {
        this.selectedTimeFrameValue = $event;
        this.onDateRangeTypeChange.emit({ key: this.selectedDateRangeTypeKey, value: this.selectedTimeFrameValue });
    }

    private updateSelectedItems() {
        this.reUpdateSelectedItems = (
            !this.studyGroupData
            && this.selectedFilterItems
            && this.selectedFilterItems.filters
            && !!Object.keys(this.selectedFilterItems.filters).length);

        if (this.studyGroupData && this.selectedFilterItems && this.selectedFilterItems.filters) {
            const studyGroupSelectedItem = this.selectedFilterItems.filters.studyGroups || [];
            this.studyGroupData = {
                ...this.studyGroupData,
                selectedItems: studyGroupSelectedItem.
                    map((id: any) => {
                        return (this.studyGroupData.listItems.find(li => li.id === id));
                    }).filter((s: SelectItem) => s)
            };

            let selectedProtocolList = this.studyGroupData.selectedItems.map(sgsi => sgsi.data.protocolCodeAndSponsorName).
                reduce((acc, val) => acc.concat(val), []);
            if (!selectedProtocolList || !selectedProtocolList.length) {
                selectedProtocolList = this.allProtocolCodeAndSponsorName;
            }
            this.selectedItemList.filters = { ...this.selectedItemList.filters, studyGroups: this.studyGroupData.selectedItems } as FilterItems;
            this.myFilterChangesAction(selectedProtocolList);
        }
    }

    private getOtherFilterData(selectedItem: PortfolioFilterRequestData[], shouldEmitEvent = false) {
        this.loadingOn();
        if (selectedItem && selectedItem.length) {
            this.getPortfolioFilterData(selectedItem, shouldEmitEvent);
            return;
        } else {
            this.sampleTrackingStorage.loadStudies()
                .subscribe(protocolData => {
                    this.allProtocolCodeAndSponsorName =
                        protocolData.map(pd => {
                            return {
                                sponsorName: pd.sponsorName,
                                protocolCode: pd.protocolCode
                            };
                        });
                    this.getPortfolioFilterData(this.allProtocolCodeAndSponsorName, shouldEmitEvent);
                    return;
                });
        }
    }

    private getPortfolioFilterData(selectedItem: PortfolioFilterRequestData[], shouldEmitEvent: boolean) {
        this.sampleTrackingStorage.getPortfolioFilterData(selectedItem)
            .subscribe(portfolioData => {
                this.studyGroupPortfolioData =
                    this.protocolFilterData =
                    this.projectFilterData =
                    this.countryFilterData =
                    portfolioData;
                this.setFilterData(portfolioData, {}, shouldEmitEvent);
                this.loadingOff();
                if (this.reUpdateSelectedItems) {
                    this.loadingOn();
                    this.updateSelectedItems();
                    this.reUpdateSelectedItems = false;
                }
            });
    }

    private getSelectedFilterData(portfolioData: ProtocolFilterData[], selectedItem: SelectItem[], searchText: string) {
        let filteredPortfolioData = portfolioData;
        if (selectedItem && selectedItem.length) {
            filteredPortfolioData = [];
            selectedItem.map(si => si.name)
                .forEach(name => {
                    filteredPortfolioData
                        .push(...portfolioData
                            .filter(sgpd => sgpd[searchText as keyof ProtocolFilterData] === name)
                        );
                });
        }
        return filteredPortfolioData;
    }

    private setFilterData(portfolioData: ProtocolFilterData[], exclude?: ExcludeFilterItems, shouldEmitEvent = false) {
        let protocolList = [] as SelectItem[];
        const projectList = [] as SelectItem[];
        const countryList = [] as SelectItem[];
        const investigatorList = [] as SelectItem[];
        const regionList = [] as SelectItem[];

        if (portfolioData) {
            portfolioData.forEach(pd => {

                    protocolList.push({
                        id: pd.protocolId,
                        name: pd.protocolCode,
                        data: {
                            protocolVisitCode: pd.protocolVisitCode,
                            protocolVisitDescription: pd.protocolVisitDescription,
                            subprotocolId: pd.subprotocolId
                        }
                    });

                    projectList.push({
                        id: pd.subprotocolId,
                        name: pd.projectNumber,
                        data: {
                            projectNumber: pd.projectNumber
                        }
                    });

                    regionList.push({
                        id: pd.region,
                        name: pd.region,
                        data: {
                            region: pd.region
                        }
                    });

                    countryList.push({
                        id: pd.countryId,
                        name: pd.countryName
                    });

                    investigatorList.push({
                        id: pd.investigatorSiteId,
                        name: pd.investigatorNumber + ' - ' + pd.investigatorName,
                        data: {
                            investigatorNumber: pd.investigatorNumber
                        }
                    });  

            });
        }

        if (!exclude || !exclude.protocol) {
            this.protocolData = this.reportcolumnService.getDropDownValueList('Protocol/Study', protocolList.filter(data => data.id != null));
        }

        if (!exclude || !exclude.project) {
            this.projectData = this.reportcolumnService.getDropDownValueList('Project №', projectList.filter(data => data.id != null));
        }

        if (!exclude || !exclude.region) {
            this.regionData = this.reportcolumnService.getDropDownValueList('Region', regionList.filter(data => data.id != null));
        }

        if (!exclude || !exclude.country) {
            this.countryData = this.reportcolumnService.getDropDownValueList('Country', countryList.filter(data => data.id != null));
        }

        if (!exclude || !exclude.investigator) {
            this.investigatorData = this.reportcolumnService.getDropDownValueList('Investigator Name/Number', investigatorList.filter(data => data.id != null));
        }

        if (this.selectedItemList.filters && Object.keys(this.selectedItemList.filters).length) {
            this.selectedFilterItems.studyGroupName = (this.selectedItemList.filters.studyGroups) ? this.selectedItemList.filters.studyGroups.map(si => si.name) : [];
            this.selectedFilterItems.protocolCode = (this.selectedItemList.filters.protocol) ? this.selectedItemList.filters.protocol.map(si => si.name) : [];
        }
        this.selectedItemList.reportType = this.selectedReportTypeId;
        if (shouldEmitEvent) {
            this.onSelectedItemChange.emit(this.selectedItemList);
        }
        this.changeDectRef.markForCheck();
    }

    private getFilterOptions(shouldEmitEvent = false) {
        this.sampleTrackingStorage.formatedStudyGroups()
            .subscribe(studyGroups => {
                this.studyGroupList = studyGroups.
                    map(studyGroup => {
                        return ({
                            id: studyGroup.id,
                            name: studyGroup.studyGroupName,
                            data: studyGroup
                        });
                    }).sort((a, b) => a.name.localeCompare(b.name));

                this.studyGroupData = {
                    title: 'Study Grouping',
                    listItems: this.studyGroupList,
                };

                this.sampleTrackingStorage.loadStudies()
                    .subscribe(protocolData => {
                        this.allProtocolCodeAndSponsorName =
                            protocolData.map(pd => {
                                return {
                                    sponsorName: pd.sponsorName,
                                    protocolCode: pd.protocolCode
                                };
                            });
                    });

                this.getOtherFilterData(this.allProtocolCodeAndSponsorName, shouldEmitEvent);
            });
    }

    private myFilterChangesAction(selectedProtocolsData: PortfolioFilterRequestData[]) {
        this.loadingOn();
        this.sampleTrackingStorage.getPortfolioFilterData(selectedProtocolsData)
            .subscribe(portfolioData => {
                this.studyGroupPortfolioData = portfolioData;
                this.setFilterData(portfolioData, {});
                this.setOtherFilterData();
                this.loadingOff();
            });
    }

    private setOtherFilterData() {
        const protocolSelectedItem = this.selectedFilterItems.filters.protocol || [];
        if (this.protocolData) {
            this.protocolData = this.reportcolumnService.getFilterData(this.protocolData, protocolSelectedItem);
            this.protocolFilterData = this.getSelectedFilterData(this.studyGroupPortfolioData, this.protocolData.selectedItems, 'protocolCode');
            this.selectedItemList.filters.protocol = this.protocolData.selectedItems;
            this.setFilterData(this.protocolFilterData, { protocol: true });

            const regionSelectedItem = this.selectedFilterItems.filters.region || [];
            this.regionData = this.reportcolumnService.getFilterData(this.regionData, regionSelectedItem);
            this.regionFilterData = this.getSelectedFilterData(this.protocolFilterData, this.regionData.selectedItems, 'region');
            this.selectedItemList.filters.region = this.regionData.selectedItems;
            this.setFilterData(this.regionFilterData, { protocol: true, region: true });

            const projectSelectedItem = this.selectedFilterItems.filters.project || [];
            this.projectData = this.reportcolumnService.getFilterData(this.projectData, projectSelectedItem);
            this.projectFilterData = this.getSelectedFilterData(this.protocolFilterData, this.projectData.selectedItems, 'projectNumber');
            this.selectedItemList.filters.project = this.projectData.selectedItems;
            this.setFilterData(this.projectFilterData, { protocol: true, region: true, project: true });

            const countrySelectedItem = this.selectedFilterItems.filters.country || [];
            this.countryData = this.reportcolumnService.getFilterData(this.countryData, countrySelectedItem);
            this.countryFilterData = this.getSelectedFilterData(this.projectFilterData, this.countryData.selectedItems, 'countryName');
            this.selectedItemList.filters.country = this.countryData.selectedItems;
            this.setFilterData(this.countryFilterData, { protocol: true, region: true, project: true, country: true });

            const investigatorSelectedItem = this.selectedFilterItems.filters.investigator || [];
            this.investigatorData = this.reportcolumnService.getFilterData(this.investigatorData, investigatorSelectedItem);
            this.selectedItemList.filters.investigator = this.investigatorData.selectedItems;

            this.changeDectRef.markForCheck();
        }
    }

    private loadingOn() {
        this.isStudyGroupLoading = true;
        this.isProtocolLoading = true;
        this.isProjectLoading = true;
        this.isRegionLoading = true;
        this.isCountryLoading = true;
        this.isInvestigatorLoading = true;
    }

    private loadingOff() {
        this.isStudyGroupLoading = false;
        this.isProtocolLoading = false;
        this.isProjectLoading = false;
        this.isRegionLoading = false;
        this.isCountryLoading = false;
        this.isInvestigatorLoading = false;
    }

    private clearDateRange() {
        this.showDateRange = false;
        this.timeFrameRanges = null;
        this.selectedDateRangeTypeKey = '';
        this.selectedTimeFrameValue = null;
        this.onDateRangeTypeChange.emit(null);
    }
}